<script setup>
// UM ITEM DE MENU — do botão direito na conversa e na mensagem.
//
// A caixa e o item são as peças do produto (`.advup-menu`,
// `.advup-menu__item`, em `_advup-tela.scss`), as mesmas da busca global e do
// chat interno. Aqui só moram as variantes que são deste menu: o ponto da
// etiqueta, o rosto do agente e o item inerte.
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  option: {
    type: Object,
    default: () => {},
  },
  variant: {
    type: String,
    default: 'default',
  },
  // advup — 2026-08-05. Item visível porém inerte, com o motivo na dica.
  // Serve para regra que o usuário não tem como adivinhar: esconder a opção
  // faz ele concluir que o sistema quebrou (foi o que aconteceu com "Editar"
  // fora da janela de 15 min do WhatsApp — virou chamado com print).
  disabled: {
    type: Boolean,
    default: false,
  },
  reason: {
    type: String,
    default: '',
  },
  // Marca a ação que DESTRÓI ("Excluir conversa", "Apagar mensagem").
  perigo: {
    type: Boolean,
    default: false,
  },
});

// Etiqueta aparece como em todo o produto: em MAIÚSCULAS e sem hífen
// (Victor, 16/09/2026 — "etiqueta eu quero sempre em letras maiúsculas").
const texto = computed(() => {
  const rotulo = props.option?.label ?? '';
  const ehEtiqueta = ['label', 'label-assigned'].includes(props.variant);
  return ehEtiqueta ? rotulo.replace(/-/g, ' ').toUpperCase() : rotulo;
});
</script>

<template>
  <div
    v-tooltip.right="disabled && reason ? reason : null"
    class="advup-menu__item mi"
    :class="{
      'mi--inerte': disabled,
      'advup-menu__item--perigo': perigo,
    }"
    role="menuitem"
    :tabindex="disabled ? -1 : 0"
    :aria-disabled="disabled"
  >
    <fluent-icon
      v-if="variant === 'icon' && option.icon"
      :icon="option.icon"
      size="16"
      class="mi__ico"
    />
    <span
      v-if="
        (variant === 'label' || variant === 'label-assigned') && option.color
      "
      class="mi__ponto"
      :style="{ backgroundColor: option.color }"
      aria-hidden="true"
    />
    <Avatar
      v-if="variant === 'agent'"
      :name="option.label"
      :src="option.thumbnail"
      :status="option.status === 'online' ? option.status : null"
      :size="20"
      class="mi__rosto"
    />
    <span class="mi__txt">{{ texto }}</span>
    <!-- O motivo do item inerte chega também a quem usa leitor de tela: a dica
         só aparece para quem passa o mouse. -->
    <span v-if="disabled && reason" class="sr-only">{{ reason }}</span>
    <Icon
      v-if="variant === 'label-assigned'"
      icon="i-lucide-check"
      class="mi__visto"
    />
  </div>
</template>

<style scoped>
.mi {
  min-width: 0;
}

.mi__ico {
  flex-shrink: 0;
  color: rgb(var(--slate-11));
  transition: color var(--mov-1) var(--mov-curva);
}

.mi:hover .mi__ico {
  color: currentcolor;
}

.mi__ponto {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  border: 1px solid rgb(var(--slate-6));
  border-radius: var(--r-redondo);
}

.mi__rosto {
  flex-shrink: 0;
}

.mi__txt {
  overflow: hidden;
  flex: 1;
  min-width: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mi__visto {
  width: var(--ico-1);
  height: var(--ico-1);
  flex-shrink: 0;
  color: rgb(var(--blue-11));
}

/* Inerte: sem realce e sem cursor de clique — tem que parecer inerte ANTES de
   a pessoa tentar, senão a descoberta vira frustração. */
.mi--inerte,
.mi--inerte:hover {
  background: transparent;
  color: rgb(var(--slate-10));
  cursor: not-allowed;
}

.mi--inerte .mi__ico {
  color: rgb(var(--slate-9));
}
</style>
